import React, { useEffect, useRef } from "react";
import styles from "./payment.module.scss";
import { useState } from "react";
import { handleGetRequest } from "../../services/GetTemplate";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { handlePutRequest } from "../../services/PutTemplate";
import { useHistory } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { PayPalScriptProvider, PayPalButtons, PayPalHostedFieldsProvider, PayPalHostedField, usePayPalHostedFields } from "@paypal/react-paypal-js";
import { handlePostRequest } from "../../services/PostTemplate";
import AmazonPayButton from "../../components/AmazonPayButton";

// Hoisted out of PaymentBulk() for the same reason as Payment.js's - a
// re-render of the parent would otherwise remount this and wipe the
// uncontrolled cardHolderName input.
const SubmitPayment = ({ paidAmount, brands, finishPayment, checked, setChecked, dispatch, closed }) => {
    const [paying, setPaying] = useState(false);
    const cardHolderName = useRef(null);
    const hostedField = usePayPalHostedFields();

    const handleClick = async () => {
        if (paidAmount === 0) {
            toast.error("Please enter valid amount to pay");
            setPaying(false);
        } else if (paidAmount > parseFloat(brands?.invoiceAmount)) {
            toast.error("Amount should be less than or equal to total amount");
            setPaying(false);
        } else if (!checked) {
            toast.error("Please agree to the terms and conditions");
            setPaying(false);
        } else {
            if (!hostedField?.cardFields) {
                setPaying(false);
            }
            const isFormInvalid = Object.values(hostedField.cardFields.getState().fields).some((field) => !field.isValid) || !cardHolderName?.current?.value;

            if (isFormInvalid) {
                toast.error("We appreciate your effort to make a payment. Please check your card information for errors, or try again in ten minutes.");
                setPaying(false);
            }
            setPaying(true);
            hostedField.cardFields
                .submit({
                    cardholderName: cardHolderName?.current?.value,
                    postalCode: hostedField?.postalCodeFields?.getState().fields.postalCode.value,
                })
                .then(async (data) => {
                    const res = await dispatch(handlePostRequest({ orderId: data?.orderId }, "/captureOrder"));
                    if (res?.success && res?.data?.purchase_units?.[0]?.payments?.captures?.[0]?.status === "COMPLETED") {
                        await finishPayment(res?.data?.id, "Card");
                        setPaying(false);
                    } else {
                        toast.error("We appreciate your effort to make a payment. Please check your card information for errors, or try again in ten minutes.");
                        setPaying(false);
                    }
                })
                .catch(() => {
                    toast.error("We appreciate your effort to make a payment. Please check your card information for errors, or try again in ten minutes.");
                    setPaying(false);
                });
        }
    };

    return (
        <>
            <div className={styles.fieldGroup}>
                <label title="This represents the full name as shown in the card">Card Holder Name</label>
                <input id="card-holder" ref={cardHolderName} className={`card-field ${styles.textInput}`} type="text" placeholder="Full name" />
            </div>
            <div className={styles.termsRow}>
                <input type="checkbox" className={styles.termsCheckbox} checked={checked ? true : false} onChange={(e) => setChecked(!checked)}></input>
                <label className={styles.termsLabel}>
                    I have read and agree to the website{" "}
                    <a href="https://zazio.co/terms/" target="_blank" rel="noreferrer">
                        terms and conditions*
                    </a>
                </label>
            </div>
            <div className={styles.payButtonRow}>
                {paying ? (
                    <div>
                        <ClipLoader color="green" loading={paying} size={50} aria-label="Loading Spinner" data-testid="loader" />
                    </div>
                ) : (
                    <button className={styles.payButton} onClick={handleClick} disabled={closed === true ? true : false}>
                        {paying ? <div className="spinner tiny" /> : `${closed ? "Please wait 1 minute" : `Pay $${paidAmount}`}`}
                    </button>
                )}
            </div>
        </>
    );
};

function PaymentBulk() {
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [clientToken, setClientToken] = useState();
    const history = useHistory();
    const [user, setUser] = useState();
    const [brands, setBrands] = useState();
    const [closed, setClosed] = useState(false);
    const [paidAmount, setPaidAmount] = useState(0);
    const [amount, setAmount] = useState(0);
    const [ids, setIds] = useState();
    const [checked, setChecked] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const dispatch = useDispatch();

    const getClientToken = async () => {
        const res = await handleGetRequest("/GetPaymentToken");
        setClientToken(res?.data);
    };
    const getInvoice = async () => {
        const res = JSON.parse(localStorage.getItem("Zazioaggregated"));
        const pays = JSON.parse(localStorage.getItem("Zaziopaid"));
        setIds(pays);
        setBrands({
            ...res,
            invoiceAmount: res?.invoiceAmount?.toFixed(2),
            paidAmount: res?.paidAmount?.toFixed(2),
        });
        setAmount(res?.invoiceAmount);
        const reva = (res?.invoiceAmount).toFixed(2);
        setPaidAmount(parseFloat(reva));
        localStorage.setItem("chargeAmount", parseFloat(reva));
    };

    useEffect(() => {
        const user = localStorage.getItem("PaymentUser");
        setUser(user);
        getInvoice();
        getClientToken();
        const expirationTime = localStorage.getItem("delayExpiration");
        if (expirationTime) {
            setClosed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkDelayExpiration = () => {
        const expirationTime = localStorage.getItem("delayExpiration");
        if (expirationTime) {
            const currentTime = new Date().getTime();
            if (currentTime > parseInt(expirationTime)) {
                localStorage.removeItem("delayExpiration");
                setClosed(false);
            }
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            checkDelayExpiration();
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Passed as the `style` prop on <PayPalHostedField> - see Payment.js's
    // identical note on why this is safe to restyle freely.
    const CUSTOM_FIELD_STYLE = {
        border: "1px solid #cbd5e1",
        height: "42px",
        borderRadius: "8px",
        marginTop: "5px",
        padding: "0 0.25rem",
    };

    const createOrder = async (data, actions) => {
        return actions.order.create({
            purchase_units: [
                {
                    amount: {
                        currency_code: "USD",
                        value: brands?.invoiceAmount,
                    },
                },
            ],
        });
    };

    const finishPayment = async (invoiceNo, paymentMethod, paymentOwner) => {
        const dat = { invoiceNo, ids, paymentMethod, ...(paymentOwner ? { paymentOwner } : {}) };
        const resp = await handlePutRequest(dat, "/bulkPay");
        if (resp?.success) {
            const delayTime = new Date().getTime() + 1 * 60 * 1000;
            localStorage.setItem("delayExpiration", delayTime);
            history.push(user === "user" ? "/Userinvoices" : "/invoices");
            localStorage.removeItem("PaymentUser");
            localStorage.removeItem("Zaziopaid");
            localStorage.removeItem("Zazioaggregated");
        } else {
            setLoading(false);
            setDisabled(false);
        }
        toast.success("Success!!! Thank you for choosing MOVE4WD. Have a wonderful rest of your day! #MoveYourWorld");
    };

    const onApprove = async (data, actions) => {
        return actions.order
            .capture()
            .then(async function (details) {
                await finishPayment(details?.id, "Paypal");
            })
            .catch(() => {
                toast.error("We appreciate your effort to make a payment. Please check your card information for errors, or try again in ten minutes.");
            });
    };

    const handlePaymentSuccess = async (id) => {
        const buyer = localStorage.getItem("buyer");
        await finishPayment(id, "AmazonPay", buyer);
        localStorage.removeItem("chargeAmount");
        localStorage.removeItem("sessionId");
    };

    // Purely presentational - same shape/conditions as the old inline JSX,
    // collapsed into one data-driven list, matching Payment.js.
    const feeRows =
        brands?.invoiceType !== "Monthly"
            ? [
                  { key: "price", label: "Cost of Goods", value: brands?.itemsPrice },
                  { key: "oversize", label: "Oversize Fee", value: brands?.oversizeFee },
                  { key: "packaging", label: "Packaging", value: brands?.packagingFee },
                  { key: "pickup", label: "Pickup", value: brands?.pickupFee },
                  { key: "delivery", label: "Delivery", value: brands?.deliveryFee },
                  { key: "handling", label: "Handling", value: brands?.handling },
                  { key: "insurance", label: "Insurance", value: brands?.insurance },
                  { key: "declaration", label: "Declaration Fee", value: brands?.declarationFee },
                  { key: "custom", label: "Custom Duties", value: brands?.customDuties },
                  { key: "dangerous", label: "Dangerous Item Fee", value: brands?.dangerousItemFee },
              ]
            : [];

    return (
        <div className={styles.pageContainer}>
            <div className={styles.contentWrap}>
                <div className={styles.amountHero}>
                    <p className={styles.amountLabel}>Amount Due</p>
                    <p className={styles.amountValue}>${paidAmount}</p>
                </div>

                <div className={styles.layout}>
                    <div className={styles.summaryCard}>
                        <button type="button" className={styles.summaryToggle} aria-expanded={summaryOpen} onClick={() => setSummaryOpen((prev) => !prev)}>
                            <span className={styles.summaryTitle}>
                                Order Summary <span className={styles.summaryTitleAmount}>(${paidAmount})</span>
                            </span>
                            <i className="pi pi-chevron-down" />
                        </button>
                        {summaryOpen && (
                            <div className={styles.summaryBody}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Weight</th>
                                            <th>Price</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {brands?.invoiceType !== "Monthly" &&
                                            brands?.packages?.map((item, index) => {
                                                let temp;
                                                let prev = brands?.packages[index - 1]?.product;
                                                if (brands?.customerType === "client") {
                                                    temp = item?.product === prev ? "Additional Pounds" : "1st Pound of Cargo";
                                                } else {
                                                    temp = "Total Pounds of Cargo";
                                                }
                                                return (
                                                    <tr className={styles.row} key={item?._id || index}>
                                                        <td>{temp}</td>
                                                        <td>{item?.weight} lb</td>
                                                        <td>${item?.price}</td>
                                                        <td>${(item?.total).toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}

                                        {/* This batch may include additional invoices (a late charge like
                                            customs duty billed separately from its parent's package) - those
                                            never carry real shipped weight, so their charges are listed here
                                            by name instead of folded into the package rows above. */}
                                        {brands?.additionalCharges?.map((charge, index) => (
                                            <tr className={styles.row} key={`additional-${index}`}>
                                                <td>{charge.label}</td>
                                                <td></td>
                                                <td></td>
                                                <td>${Number(charge.amount).toFixed(2)}</td>
                                            </tr>
                                        ))}

                                        {feeRows.map(
                                            (row) =>
                                                row.value !== 0 && (
                                                    <tr className={styles.row} key={row.key}>
                                                        <td className={styles.addOnLabelCell}>{row.label}</td>
                                                        <td></td>
                                                        <td></td>
                                                        <td>${row.value}</td>
                                                    </tr>
                                                ),
                                        )}

                                        {brands?.invoiceType === "Monthly" && (
                                            <tr className={styles.row}>
                                                <td>Monthly Fee</td>
                                                <td></td>
                                                <td></td>
                                                <td>${brands?.MonthlyFee}</td>
                                            </tr>
                                        )}
                                        {brands?.invoiceType === "Monthly" && (
                                            <tr className={styles.row}>
                                                <td>Website usage Fee</td>
                                                <td></td>
                                                <td></td>
                                                <td>${brands?.WebsiteFee}</td>
                                            </tr>
                                        )}

                                        <tr className={`${styles.row} ${styles.subtotalRow}`}>
                                            <td>Sub-Total</td>
                                            <td></td>
                                            <td></td>
                                            <td>${brands?.customerType === "partner" || brands?.customerType === "admin" ? brands?.TotalAmount : brands?.invoiceAmount}</td>
                                        </tr>
                                        {brands?.customerType === "partner" ||
                                            (brands?.customerType === "admin" && (
                                                <tr className={styles.row}>
                                                    <td>{brands?.customerType === "partner" ? "Credit Discount" : "Admin Discount"}</td>
                                                    <td></td>
                                                    <td></td>
                                                    <td>- ${brands?.discountedAmount}</td>
                                                </tr>
                                            ))}

                                        <tr className={styles.row}>
                                            <td>Tax</td>
                                            <td></td>
                                            <td></td>
                                            <td>$0</td>
                                        </tr>

                                        {brands?.paymentStatus === "pending" && !(brands?.paidAmount > 0) ? (
                                            <tr className={`${styles.row} ${styles.totalRow}`}>
                                                <td>Total</td>
                                                <td></td>
                                                <td></td>
                                                <td>${brands?.invoiceAmount}</td>
                                            </tr>
                                        ) : null}
                                        {brands?.paymentStatus === "pending" && brands?.paidAmount > 0 ? (
                                            <>
                                                <tr className={styles.row}>
                                                    <td>Paid Amount</td>
                                                    <td></td>
                                                    <td></td>
                                                    <td>${brands?.paidAmount}</td>
                                                </tr>
                                                <tr className={`${styles.row} ${styles.totalRow}`}>
                                                    <td>Remaining Amount</td>
                                                    <td></td>
                                                    <td></td>
                                                    <td>${brands?.invoiceAmount}</td>
                                                </tr>
                                            </>
                                        ) : null}
                                        {brands?.paymentStatus === "paid" ? (
                                            <tr className={`${styles.row} ${styles.totalRow}`}>
                                                <td>Total</td>
                                                <td></td>
                                                <td></td>
                                                <td>${brands?.paidAmount}</td>
                                            </tr>
                                        ) : null}
                                        {!brands?.paymentStatus && (
                                            <tr className={`${styles.row} ${styles.totalRow}`}>
                                                <td>Total</td>
                                                <td></td>
                                                <td></td>
                                                <td>${brands?.invoiceAmount}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className={styles.expressRow}>
                        {clientToken && amount > 0 && (
                            <PayPalScriptProvider
                                options={{
                                    clientId: "AXOElk_LhRFd7dSbyMw3lhfQvGcngnNi56aKp9vyicSf6VfRqi2cdw4Cp8DSvrxArDl8Op8CKBGifxAi",
                                    dataClientToken: clientToken,
                                    components: "hosted-fields,buttons",
                                    intent: "capture",
                                    vault: false,
                                    currency: "USD",
                                }}
                                onError={(err) => {
                                    console.log(err);
                                }}
                            >
                                <div className={styles.expressButton}>
                                    <p className={styles.expressLabel}>
                                        <i className="pi pi-wallet" /> PayPal
                                    </p>
                                    {brands && <PayPalButtons style={{ layout: "horizontal", shape: "pill" }} createOrder={createOrder} onApprove={onApprove}></PayPalButtons>}
                                </div>
                            </PayPalScriptProvider>
                        )}
                        {amount > 0 && (
                            <div className={styles.expressButton}>
                                <p className={styles.expressLabel}>
                                    <i className="pi pi-amazon" /> Amazon Pay
                                </p>
                                <div className={styles.amazonWrap}>
                                    <AmazonPayButton handlePaymentSuccess={handlePaymentSuccess} amount={paidAmount} />
                                </div>
                            </div>
                        )}
                    </div>

                    {clientToken && amount > 0 && (
                        <div className={styles.cardSection}>
                            <p className={styles.columnTitle}>
                                <i className="pi pi-credit-card" /> Pay by Card
                            </p>
                            {/* Separate PayPalScriptProvider instance - see Payment.js's identical
                                note on why the SDK loader dedupes this rather than loading twice. */}
                            <PayPalScriptProvider
                                options={{
                                    clientId: "AXOElk_LhRFd7dSbyMw3lhfQvGcngnNi56aKp9vyicSf6VfRqi2cdw4Cp8DSvrxArDl8Op8CKBGifxAi",
                                    dataClientToken: clientToken,
                                    components: "hosted-fields,buttons",
                                    intent: "capture",
                                    vault: false,
                                    currency: "USD",
                                }}
                                onError={(err) => {
                                    console.log(err);
                                }}
                            >
                                <PayPalHostedFieldsProvider
                                    createOrder={async function () {
                                        if (amount === 0) {
                                            toast.error("We appreciate your effort to make a payment. Please check your card information or try again in ten minutes.");
                                        } else {
                                            const res = await dispatch(handlePostRequest({ amount }, "/createOrder", false, false));
                                            return res?.data?.id;
                                        }
                                    }}
                                    onError={(err) => {
                                        toast.error("We appreciate your effort to make a payment. Please check your card information or try again in ten minutes.");
                                        setLoading(false);
                                    }}
                                >
                                    <div className={styles.fieldGroup}>
                                        <label htmlFor="card-number">
                                            Card Number
                                            <span className={styles.requiredMark}>*</span>
                                        </label>
                                    </div>
                                    <PayPalHostedField
                                        id="card-number"
                                        className="card__number"
                                        style={CUSTOM_FIELD_STYLE}
                                        hostedFieldType="number"
                                        options={{ selector: "#card-number", placeholder: "4111 1111 1111 1111" }}
                                    />
                                    <div className={styles.fieldGroup}>
                                        <label htmlFor="cvv">
                                            CVV<span className={styles.requiredMark}>*</span>
                                        </label>
                                    </div>
                                    <PayPalHostedField
                                        id="cvv"
                                        className="card-field"
                                        style={CUSTOM_FIELD_STYLE}
                                        hostedFieldType="cvv"
                                        options={{ selector: "#cvv", placeholder: "123", maskInput: true }}
                                    />
                                    <div className={styles.fieldGroup}>
                                        <label htmlFor="expiration-date">
                                            Expiration Date
                                            <span className={styles.requiredMark}>*</span>
                                        </label>
                                    </div>
                                    <PayPalHostedField
                                        id="expiration-date"
                                        className="card-field"
                                        style={CUSTOM_FIELD_STYLE}
                                        hostedFieldType="expirationDate"
                                        options={{ selector: "#expiration-date", placeholder: "MM/YYYY" }}
                                    />
                                    <div className={styles.fieldGroup}>
                                        <label htmlFor="postal-code">
                                            Postal Code<span className={styles.requiredMark}>*</span>
                                        </label>
                                    </div>
                                    <PayPalHostedField
                                        id="postal-code"
                                        className="postal-field"
                                        style={CUSTOM_FIELD_STYLE}
                                        hostedFieldType="postalCode"
                                        options={{ selector: "#postal-code", placeholder: "00000" }}
                                    />
                                    <p className={styles.postalNote}>PLEASE USE 00000 for the Postal Code when using a Jamaican Debit/Credit Card to make payment.</p>

                                    <SubmitPayment
                                        paidAmount={paidAmount}
                                        brands={brands}
                                        finishPayment={finishPayment}
                                        checked={checked}
                                        setChecked={setChecked}
                                        dispatch={dispatch}
                                        closed={closed}
                                    />
                                </PayPalHostedFieldsProvider>
                            </PayPalScriptProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PaymentBulk;
